import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, X, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import loginVideo from "@/assets/login-video.webm";
import Seo from "@/components/Seo";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"signin" | "forgot">("signin");
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string; pending?: boolean } | null;
  const from = state?.from;
  const redirectTo = from || "/account";
  const fromPricing = from === "/pricing";
  const showPendingBanner = state?.pending === true;

  const emailError =
    emailTouched && email.length > 0 && !EMAIL_REGEX.test(email)
      ? "Hmm, det där ser inte ut som en giltig e-postadress"
      : "";

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailTouched(true);
      return;
    }
    setLoading(true);
    // Visa alltid samma svar — annars går det att gissa vilka adresser som har konto.
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error && !/user|not found/i.test(error.message)) {
      toast.error("Kunde inte skicka mejlet just nu. Försök igen om en stund.");
      return;
    }
    setResetSent(true);
  };

  const backToSignIn = () => {
    setMode("signin");
    setResetSent(false);
    setPassword("");
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      toast.error(error.message);
      return;
    }

    const userId = signInData.user?.id;
    if (userId) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("status")
        .eq("id", userId)
        .maybeSingle();

      if (!profile || profile.status === "pending") {
        await supabase.auth.signOut();
        setLoading(false);
        toast.error("Ditt konto väntar på godkännande. Vi hör av oss inom 24h.");
        return;
      }
      if (profile.status === "rejected") {
        await supabase.auth.signOut();
        setLoading(false);
        toast.error("Din kontoansökan har avslagits. Kontakta oss för mer information.");
        return;
      }
    }

    setLoading(false);
    toast.success("Välkommen tillbaka!");
    navigate(redirectTo);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <Seo title="Logga in – Aquadias" description="Logga in på ditt aQuadias-konto för att hantera prenumeration och beställningar." path="/login" />
      <div className="hidden md:block">
        <video src={loginVideo} autoPlay loop muted playsInline className="w-full h-full object-cover" />
      </div>

      <div className="flex flex-col bg-gradient-to-b from-coral-400/30 via-terracotta-100/40 to-cream-100">
        <div className="flex justify-between items-center px-6 py-4">
          <Link to="/" className="p-2 rounded-full hover:bg-white/30 transition-colors">
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </Link>
          <Link to="/" className="text-xl font-bold tracking-wide text-foreground">aQuadias</Link>
          <Link to="/" className="p-2 rounded-full hover:bg-white/30 transition-colors">
            <X className="w-5 h-5 text-foreground" />
          </Link>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-full max-w-md space-y-8 text-center">
            <div>
              <span className="font-serif italic text-5xl md:text-6xl text-foreground">
                {mode === "forgot" ? "Glömt" : "Välkommen"}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-wider text-foreground mt-2">
                {mode === "forgot" ? "lösenordet?" : "tillbaka!"}
              </h1>
            </div>

            {mode === "signin" && showPendingBanner && (
              <p className="text-xs uppercase tracking-widest text-foreground/80 bg-white/40 backdrop-blur-sm rounded-lg px-4 py-3">
                Ditt konto väntar på godkännande — vi hör av oss inom 24h
              </p>
            )}

            {mode === "signin" && fromPricing && !showPendingBanner && (
              <p className="text-xs uppercase tracking-widest text-foreground/80 bg-white/40 backdrop-blur-sm rounded-lg px-4 py-3">
                Logga in för att slutföra din beställning
              </p>
            )}

            {mode === "forgot" && resetSent ? (
              <div className="space-y-6 text-left">
                <p className="text-sm text-foreground/80 bg-white/40 backdrop-blur-sm rounded-lg px-5 py-4">
                  Om adressen finns hos oss har vi skickat ett mejl med en länk där du väljer ett
                  nytt lösenord. Kolla även skräpposten.
                </p>
                <button
                  type="button"
                  onClick={backToSignIn}
                  className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-widest text-foreground/70 hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" /> Tillbaka till inloggning
                </button>
              </div>
            ) : (
              <form
                onSubmit={mode === "forgot" ? handleForgot : handleSubmit}
                className="space-y-4 text-left"
              >
                {mode === "forgot" && (
                  <p className="text-sm text-foreground/80">
                    Skriv in din e-postadress så skickar vi en länk där du väljer ett nytt lösenord.
                  </p>
                )}
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    placeholder="E-POST"
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? "login-email-error" : undefined}
                    className={`w-full bg-white/50 backdrop-blur-sm border rounded-lg px-5 py-4 text-sm tracking-widest placeholder:text-muted-foreground/70 placeholder:tracking-widest placeholder:uppercase focus:outline-none focus:ring-2 focus:ring-ring/30 transition-all ${
                      emailError ? "border-destructive/60" : "border-transparent"
                    }`}
                    required
                  />
                  {emailError && (
                    <p id="login-email-error" className="mt-2 px-2 text-xs text-destructive/90">
                      {emailError}
                    </p>
                  )}
                </div>

                {mode === "signin" && (
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="LÖSENORD"
                      className="w-full bg-white/50 backdrop-blur-sm border-0 rounded-lg px-5 py-4 pr-12 text-sm tracking-widest placeholder:text-muted-foreground/70 placeholder:tracking-widest placeholder:uppercase focus:outline-none focus:ring-2 focus:ring-ring/30 transition-all"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => (mode === "forgot" ? backToSignIn() : setMode("forgot"))}
                  className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-widest text-foreground/70 hover:text-foreground transition-colors"
                >
                  {mode === "forgot" ? (
                    <>
                      <ArrowLeft className="w-3 h-3" /> Tillbaka till inloggning
                    </>
                  ) : (
                    <>
                      Glömt lösenord <ArrowRight className="w-3 h-3" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="px-6 pb-8 w-full max-w-md mx-auto space-y-4">
          {!(mode === "forgot" && resetSent) && (
            <button
              type="submit"
              onClick={(mode === "forgot" ? handleForgot : handleSubmit) as any}
              disabled={loading}
              className="w-full border border-foreground text-foreground bg-transparent py-3.5 rounded-lg font-medium uppercase tracking-widest text-sm hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "forgot" ? (
                <>Skicka återställningslänk <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>Logga in <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          )}
          {mode === "signin" && (
            <p className="text-center text-sm text-muted-foreground">
              Inget konto <em>än?</em>{" "}
              <Link to="/register" className="uppercase font-semibold tracking-wider text-foreground hover:underline inline-flex items-center gap-1">
                Registrera dig <ArrowRight className="w-3 h-3" />
              </Link>
            </p>
          )}
        </div>

      </div>
    </div>
  );
};

export default LoginPage;
